import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Database, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export type EntityKey = "ageGroups" | "adults" | "children" | "promotions" | "registrations" | "reservations";

const ENTITY_LABELS: Record<EntityKey, string> = {
  ageGroups: "Groupes d'âges",
  adults: "Adultes",
  children: "Enfants",
  promotions: "Promotions",
  registrations: "Inscriptions",
  reservations: "Réservations",
};

export function SeedPopover() {
  const { data: countsResp, isLoading: loadingCounts, refetch } = api.useQuery("get", "/seed/counts");
  const counts = (countsResp?.data as Record<EntityKey, number>) || null;
  const { mutateAsync: executeSeed, isPending: isExecuting } = api.useMutation("post", "/seed/execute");

  const [plan, setPlan] = useState<Record<EntityKey, number>>({
    ageGroups: 0,
    adults: 0,
    children: 0,
    promotions: 0,
    registrations: 0,
    reservations: 0,
  });

  const hasAnyQuantity = useMemo(() => Object.values(plan).some((v) => v > 0), [plan]);

  // Press-and-hold support for +/-
  const holdTimer = useRef<number | null>(null);
  const repeatTimer = useRef<number | null>(null);

  const clearTimers = () => {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    if (repeatTimer.current) {
      window.clearInterval(repeatTimer.current);
      repeatTimer.current = null;
    }
  };

  const startAdjustHold = (key: EntityKey, delta: number) => {
    // immediate step
    setPlan((p) => ({ ...p, [key]: Math.max(0, (p[key] ?? 0) + delta) }));

    // start repeating after short delay
    clearTimers();
    holdTimer.current = window.setTimeout(() => {
      repeatTimer.current = window.setInterval(() => {
        setPlan((p) => ({ ...p, [key]: Math.max(0, (p[key] ?? 0) + delta) }));
      }, 60);
    }, 300);
  };

  const stopAdjustHold = () => clearTimers();

  const handleInputChange = (key: EntityKey, value: string) => {
    // numeric-only text input: strip non-digits
    const sanitized = value.replace(/\D+/g, "");
    const n = sanitized === "" ? 0 : parseInt(sanitized, 10);
    setPlan((p) => ({ ...p, [key]: Math.max(0, isNaN(n) ? 0 : n) }));
  };

  const handleExecute = async () => {
    if (!hasAnyQuantity) {
      toast.info("Aucune quantité sélectionnée");
      return;
    }

    try {
      const body: Partial<Record<EntityKey, number>> = {};
      (Object.keys(plan) as EntityKey[]).forEach((k) => {
        if (plan[k] > 0) body[k] = plan[k];
      });
      const json = await executeSeed({ body });
      const d = json.data as Record<EntityKey, number>;
      toast.success("Seeding exécuté", {
        description: `+${d.ageGroups} groupes, +${d.adults} adultes, +${d.children} enfants, +${d.promotions} promos, +${d.registrations} inscriptions, +${d.reservations} réservations`,
      });
      setPlan({ ageGroups: 0, adults: 0, children: 0, promotions: 0, registrations: 0, reservations: 0 });
      await refetch();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Echec de l'exécution";
      toast.error("Erreur lors du seeding", { description: message });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="w-full bg-green-600 text-white hover:bg-green-700" size="sm">
          <Database size={16} className="mr-2" />
          Peupler la DB
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="start" onPointerUp={stopAdjustHold}>
        <div className="space-y-3">
          <div>
            <h4 className="leading-none font-medium">Peuplement ciblé</h4>
            <p className="text-sm text-muted-foreground">Choisissez combien créer par entité.</p>
          </div>
          <div className="space-y-2">
            {(["ageGroups", "adults", "children", "promotions", "registrations", "reservations"] as EntityKey[]).map((key) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Label className="min-w-40 text-sm font-medium">{ENTITY_LABELS[key]}</Label>
                  <span className="text-xs text-muted-foreground">
                    {loadingCounts ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={12} className="animate-spin" /> Chargement...
                      </span>
                    ) : (
                      <>{counts?.[key] ?? 0} existants</>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onMouseDown={() => startAdjustHold(key, -1)}
                    onMouseUp={stopAdjustHold}
                    onMouseLeave={stopAdjustHold}
                    onTouchStart={() => startAdjustHold(key, -1)}
                    onTouchEnd={stopAdjustHold}
                    disabled={plan[key] <= 0}
                  >
                    <Minus size={14} />
                  </Button>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={String(plan[key])}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    className="h-8 w-14 text-center"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onMouseDown={() => startAdjustHold(key, 1)}
                    onMouseUp={stopAdjustHold}
                    onMouseLeave={stopAdjustHold}
                    onTouchStart={() => startAdjustHold(key, 1)}
                    onTouchEnd={stopAdjustHold}
                  >
                    <Plus size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleExecute} disabled={isExecuting || !hasAnyQuantity}>
              {isExecuting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {isExecuting ? "Exécution..." : "Exécuter"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
